import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Sparkles, Wand2, BookOpen, Star } from "lucide-react";

const CodexCard = ({ c, lang, t, index }) => {
  const img = c.image_path ? fileUrl(c.image_path) : c.image_url || null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 16, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay: index * 0.06, duration: 0.4 }}
      whileHover={{ y: -4 }}
      className="relative overflow-hidden rounded-2xl border bg-[#0A0F1D]/80 p-5"
      style={{ borderColor: `${c.color}44`, boxShadow: `0 0 28px ${c.color}1e` }}
      data-testid="bestiary-card"
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: `radial-gradient(circle at 85% 0%, ${c.color}18, transparent 55%)` }}
      />
      <div className="flex items-start justify-between gap-3">
        {img ? (
          <div
            className="h-20 w-20 shrink-0 overflow-hidden rounded-xl"
            style={{ border: `1px solid ${c.color}66`, boxShadow: `0 0 16px ${c.color}44` }}
          >
            <img src={img} alt={c.name} className="h-full w-full object-cover" data-testid="bestiary-card-image" />
          </div>
        ) : (
          <div
            className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full"
            style={{ background: `radial-gradient(circle at 50% 40%, ${c.color}33, transparent 75%)`, border: `1px solid ${c.color}55` }}
          >
            <Sparkles className="h-7 w-7" style={{ color: c.color }} />
          </div>
        )}
        <div className="flex items-center gap-2">
          <span
            data-testid="creature-count-badge"
            className="rounded-full px-2.5 py-0.5 font-serif text-lg font-semibold"
            style={{ background: `${c.color}22`, color: c.color, border: `1px solid ${c.color}55`, boxShadow: `0 0 12px ${c.color}33` }}
          >
            ×{c.count}
          </span>
          <span
            className="rounded-full px-2.5 py-0.5 text-xs"
            style={{ background: `${c.color}1a`, color: c.color, border: `1px solid ${c.color}33` }}
          >
            {c.region_label?.[lang] || c.region_label?.zh}
          </span>
        </div>
      </div>
      <p className="mt-3 font-serif text-xl" style={{ color: c.color }}>{c.name}</p>
      <p className="mt-1 text-sm leading-relaxed text-[#A3B1C6]">{c.description}</p>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#A3B1C6]">
        <span className="flex items-center gap-1">
          <Star className="h-3.5 w-3.5 text-[#D4AF37]" />
          {t("creatures.met_times")} {c.count} {t("creatures.times_unit")}
        </span>
        <span>{t("creatures.first_met")}：{c.first_seen}</span>
      </div>
      {c.moods?.length > 0 && (
        <div className="mt-2 flex gap-1 text-base">{c.moods.map((m, i) => <span key={i}>{m}</span>)}</div>
      )}
    </motion.div>
  );
};

const CreatureCard = ({ c, image, trailLabel }) => (
  <motion.div
    whileHover={{ y: -4 }}
    className="overflow-hidden rounded-2xl border border-white/10 bg-[#0A0F1D]/80"
    style={{ boxShadow: `0 0 24px ${c.color}22` }}
    data-testid="creature-card"
  >
    {image ? (
      <div className="relative aspect-square w-full overflow-hidden">
        <img src={image} alt={c.name} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1D] via-transparent to-transparent" />
        <div
          className="absolute inset-x-0 bottom-0 h-1"
          style={{ background: `linear-gradient(90deg, transparent, ${c.color}, transparent)` }}
        />
      </div>
    ) : (
      <div
        className="flex aspect-square w-full items-center justify-center"
        style={{ background: `radial-gradient(circle at 50% 40%, ${c.color}22, transparent 70%)` }}
      >
        <Sparkles className="h-16 w-16" style={{ color: c.color }} />
      </div>
    )}
    <div className="p-5">
      <p className="font-serif text-xl" style={{ color: c.color }}>{c.name}</p>
      {c.concept && c.concept !== c.name && (
        <p className="text-xs text-[#A3B1C6]/60">{c.concept}</p>
      )}
      <p className="text-xs text-[#A3B1C6]">{trailLabel}：{c.trail_style}</p>
      <p className="mt-3 text-sm leading-relaxed text-[#A3B1C6]">{c.description}</p>
      {c.emotions?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {c.emotions.map((e) => (
            <span key={e} className="rounded-full bg-white/5 px-2.5 py-0.5 text-xs text-[#A3B1C6]">{e}</span>
          ))}
        </div>
      )}
    </div>
  </motion.div>
);

export default function Creatures() {
  const { t, lang } = useT();
  const [base, setBase] = useState([]);
  const [custom, setCustom] = useState([]);
  const [codex, setCodex] = useState(null);

  useEffect(() => {
    api.get("/creatures").then((r) => {
      setBase(r.data.base);
      setCustom(r.data.custom);
    });
    api.get("/creatures/collection").then((r) => setCodex(r.data));
  }, []);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-serif text-4xl font-semibold tracking-tighter sm:text-5xl">
          <span className="gold-text">{t("creatures.title")}</span>
        </h1>
        <p className="mt-2 text-sm text-[#A3B1C6]">
          {t("creatures.sub")}
        </p>
      </div>

      <div data-testid="bestiary-section" className="rounded-2xl border border-[#D4AF37]/25 bg-[#0A0F1D]/80 p-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="font-serif text-2xl text-[#FDFBF7]">{t("creatures.codex_t")}</p>
            <p className="mt-1 text-sm text-[#A3B1C6]">{t("creatures.codex_s")}</p>
          </div>
          {codex && codex.total > 0 && (
            <div className="flex gap-5 text-sm" data-testid="bestiary-count">
              <span className="text-[#A3B1C6]">
                {t("creatures.codex_count")}<span className="gold-text ml-2 font-serif text-2xl">{codex.total}</span>
              </span>
              <span className="text-[#A3B1C6]">
                {t("creatures.codex_enc")}<span className="ml-2 font-serif text-2xl text-[#9D4CDD]">{codex.encounters}</span>
              </span>
            </div>
          )}
        </div>
        {codex && codex.total > 0 ? (
          <div className="mt-5 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {codex.items.map((c, i) => (
              <CodexCard key={c.name} c={c} lang={lang} t={t} index={i} />
            ))}
          </div>
        ) : (
          <div className="mt-5 flex flex-col items-center gap-4 rounded-xl border border-dashed border-white/10 py-10 text-center">
            <BookOpen className="h-10 w-10 text-[#D4AF37]/60" />
            <p className="max-w-md text-sm text-[#A3B1C6]">{t("creatures.codex_empty")}</p>
            <Link to="/chat">
              <Button data-testid="bestiary-go-diary" className="rounded-full bg-[#D4AF37] px-6 text-[#050814] hover:opacity-90">
                <Wand2 className="mr-1 h-4 w-4" />{t("creatures.go_diary")}
              </Button>
            </Link>
          </div>
        )}
      </div>

      {custom.length > 0 && (
        <div>
          <h2 className="mb-3 font-serif text-2xl text-[#FDFBF7]">{t("creatures.my")}</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {custom.map((c) => (
              <CreatureCard key={c.id} c={c} trailLabel={t("creatures.trail")} image={c.image_path ? fileUrl(c.image_path) : null} />
            ))}
          </div>
        </div>
      )}

      <div>
        <h2 className="mb-3 font-serif text-2xl text-[#FDFBF7]">{t("creatures.base")}</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {base.map((c) => (
            <CreatureCard key={c.key} c={c} trailLabel={t("creatures.trail")} image={c.image_url || null} />
          ))}
        </div>
      </div>
    </div>
  );
}
